import { type AppointmentListItem } from '@/api';
import { AppointmentCard } from './AppointmentCard';
import { AppointmentListSkeleton } from './skeletons/AppointmentListSkeleton';
import { EmptyAppointments } from './EmptyAppointments';
import { AppointmentError } from './AppointmentError';

interface AppointmentListProps {
  appointments: AppointmentListItem[];
  isLoading?: boolean;
  isError?: boolean;
  error?: Error | null;
  userRole: 'patient' | 'doctor';
  emptyTitle?: string;
  emptyDescription?: string;
  emptyActionLabel?: string;
  onEmptyAction?: () => void;
  onCancel?: (id: string) => void;
  onReschedule?: (id: string) => void;
  onConfirm?: (id: string) => void;
  onJoin?: (id: string) => void;
  onRetry?: () => void;
}

export function AppointmentList({
  appointments,
  isLoading = false,
  isError = false,
  error = null,
  userRole,
  emptyTitle,
  emptyDescription,
  emptyActionLabel,
  onEmptyAction,
  onCancel,
  onReschedule,
  onConfirm,
  onJoin,
  onRetry,
}: AppointmentListProps) {
  if (isLoading) {
    return <AppointmentListSkeleton count={3} />;
  }

  if (isError) {
    return <AppointmentError message={error?.message} onRetry={onRetry} />;
  }

  if (appointments.length === 0) {
    return (
      <EmptyAppointments
        title={emptyTitle}
        description={emptyDescription}
        actionLabel={emptyActionLabel}
        onAction={onEmptyAction}
      />
    );
  }

  return (
    <div className="space-y-3">
      {appointments.map((appointment) => (
        <AppointmentCard
          key={appointment.id}
          appointment={appointment}
          userRole={userRole}
          onCancel={onCancel}
          onReschedule={onReschedule}
          onConfirm={onConfirm}
          onJoin={onJoin}
        />
      ))}
    </div>
  );
}
